// De breng- en haaltijden van de vestiging (Instellingen › Pension): wanneer dieren gebracht en opgehaald kunnen
// worden. De wizard Nieuwe reservering biedt alleen die tijden aan; zolang een vestiging niets heeft ingericht,
// kiest de balie vrij tussen 07:00 en 20:00.
import { useCustom } from "@refinedev/core";

import { useLocatieUrl } from "./locatie";

export interface Venster {
  van: string;
  tot: string;
}

/** 1 is maandag, 7 is zondag. */
export interface TijdenDag {
  dag: number;
  brengen: Venster[];
  halen: Venster[];
}

export interface Tijden {
  vestigingId: number | null;
  vestigingCode: string | null;
  stapMinuten: number;
  ingesteld: boolean;
  dagen: TijdenDag[];
}

export type Tijdsoort = "brengen" | "halen";

/** Zonder ingerichte tijden: van 07:00 tot en met 20:00, per half uur. */
export const VRIJE_TIJDEN = Array.from({ length: 27 }, (_, i) => klok(7 * 60 + i * 30));

export function klok(minuten: number): string {
  const m = ((minuten % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** "10:00:00" en "10:00" worden allebei "10:00". */
export function korteTijd(tijd: string | null | undefined): string {
  return (tijd ?? "").slice(0, 5);
}

export function minutenVan(tijd: string): number {
  const [u, m] = korteTijd(tijd).split(":");
  return Number(u) * 60 + Number(m);
}

export function legeTijden(): Tijden {
  return {
    vestigingId: null,
    vestigingCode: null,
    stapMinuten: 30,
    ingesteld: false,
    dagen: Array.from({ length: 7 }, (_, i) => ({ dag: i + 1, brengen: [], halen: [] })),
  };
}

/** De tijden van de vestiging; zolang de api niets zegt, de lege instelling. */
export function useBrengHaaltijden(): Tijden {
  const locatie = useLocatieUrl();
  const data = useCustom<Tijden>({ url: locatie("pension/breng-haaltijden"), method: "get" }).data?.data;
  return data && Array.isArray(data.dagen) ? data : legeTijden();
}

/** Maandag is 1, zondag is 7 — zoals de instelling ze telt. */
export function dagVan(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 ? 7 : d;
}

export function venstersVoor(tijden: Tijden, iso: string, soort: Tijdsoort): Venster[] {
  if (!tijden.ingesteld || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return [];
  const dag = tijden.dagen.find((d) => d.dag === dagVan(iso));
  return (soort === "brengen" ? dag?.brengen : dag?.halen) ?? [];
}

/**
 * De tijden die je op deze dag kunt kiezen. Zonder ingerichte tijden zijn dat de vrije tijden; op een dag zonder
 * blokken is de lijst leeg — dan brengt of haalt het pension die dag niet.
 */
export function tijdenVoor(tijden: Tijden, iso: string, soort: Tijdsoort): string[] {
  if (!tijden.ingesteld) return VRIJE_TIJDEN;
  const stap = tijden.stapMinuten > 0 ? tijden.stapMinuten : 30;
  const uit: string[] = [];
  for (const v of venstersVoor(tijden, iso, soort)) {
    for (let m = minutenVan(v.van); m <= minutenVan(v.tot); m += stap) {
      const tijd = klok(m);
      if (!uit.includes(tijd)) uit.push(tijd);
    }
  }
  return uit.sort();
}

/** Of het pension op deze dag dieren aanneemt of meegeeft. */
export function open(tijden: Tijden, iso: string, soort: Tijdsoort): boolean {
  return !tijden.ingesteld || tijdenVoor(tijden, iso, soort).length > 0;
}

/** "10:00 – 16:00", of met meerdere blokken "09:00 – 12:00 en 14:00 – 18:00". */
export function vensterTekst(vensters: Venster[], en: string): string {
  const delen = vensters.map((v) => `${korteTijd(v.van)} – ${korteTijd(v.tot)}`);
  if (delen.length <= 1) return delen.join("");
  return `${delen.slice(0, -1).join(", ")} ${en} ${delen[delen.length - 1]}`;
}
